import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { MoneyInput } from '../ui/money-input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { AlertCircle } from 'lucide-react';
import { transactionService } from '../../services/transactionService';
import cashBoxService, { CashBox } from '../../services/cashBoxService';
import { CreateExpenseRequest } from '../../types';
import { showError } from '../Alerts';

interface ExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  preselectedCashBoxId?: number;
}

/**
 * Modal para crear egresos de caja (gastos)
 * POST /api/transactions/expense
 */
export default function ExpenseModal({
  isOpen,
  onClose,
  onSuccess,
  preselectedCashBoxId,
}: ExpenseModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cashBoxes, setCashBoxes] = useState<CashBox[]>([]);

  // Form state
  const [formData, setFormData] = useState<CreateExpenseRequest>({
    cashBoxId: 0,
    amount: 0,
    description: '',
  });

  // Cargar cajas disponibles
  useEffect(() => {
    const loadCashBoxes = async () => {
      if (isOpen) {
        try {
          const boxes = await cashBoxService.getAllCashBoxes();
          const active = boxes.filter((box) => box.isActive);
          setCashBoxes(active);
          if (preselectedCashBoxId) {
            setFormData(prev => ({ ...prev, cashBoxId: preselectedCashBoxId }));
          }
        } catch (error) {
          console.error('Error al cargar cajas:', error);
          setError('Error al cargar las cajas disponibles');
        }
      }
    };

    loadCashBoxes();
  }, [isOpen, preselectedCashBoxId]);

  // Resetear formulario al cerrar
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        cashBoxId: 0,
        amount: 0,
        description: '',
      });
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validaciones
    if (!formData.cashBoxId) {
      setError('Debe seleccionar una caja');
      return;
    }

    if (formData.amount <= 0) {
      setError('El monto debe ser mayor a cero');
      return;
    }

    const selectedBox = cashBoxes.find((box) => box.id === formData.cashBoxId);
    if (selectedBox && selectedBox.balance < formData.amount) {
      setError(
        `Saldo insuficiente. Saldo actual: ${selectedBox.currency} ${selectedBox.balance.toFixed(2)}`
      );
      return;
    }

    try {
      setLoading(true);
      await transactionService.createExpense(formData);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error al crear egreso:', error);
      const msg = error.response?.data?.message || 'Error al registrar el egreso. Por favor, intente nuevamente.';
      setError(msg);
      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return `${currency} ${amount.toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const selectedCashBox = cashBoxes.find(
    (box) => box.id === formData.cashBoxId
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Registrar Egreso de Caja</DialogTitle>
            <DialogDescription>
              Registrar una salida de dinero de una caja hacia &quot;Gastos Varios&quot;.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {error && (
              <div className="flex items-center gap-2 p-3 text-sm text-red-800 bg-red-100 rounded-md">
                <AlertCircle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            )}

            <div className="grid gap-2">
              <Label htmlFor="cashBox">Caja *</Label>
              <Select
                value={formData.cashBoxId.toString()}
                onValueChange={(value) =>
                  setFormData({ ...formData, cashBoxId: parseInt(value) })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar caja" />
                </SelectTrigger>
                <SelectContent>
                  {cashBoxes.map((box) => (
                    <SelectItem key={box.id} value={box.id.toString()}>
                      {box.name} - {formatCurrency(box.balance, box.currency)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedCashBox && (
                <p className="text-sm text-gray-500">
                  Saldo disponible:{' '}
                  {formatCurrency(
                    selectedCashBox.balance,
                    selectedCashBox.currency
                  )}
                </p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="amount">Monto *</Label>
              <MoneyInput
                value={formData.amount === 0 ? '' : formData.amount}
                onValueChange={(value) =>
                  setFormData({ ...formData, amount: value ?? 0 })
                }
                currency={selectedCashBox?.currency || 'ARS'}
                placeholder="0,00"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                placeholder="Ej: Compra de insumos de oficina"
                rows={3}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Registrando...' : 'Registrar Egreso'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
